/**
 * Клиент кэша для воркеров.
 * Общается с Master через IPC для получения/сохранения закэшированных данных.
 */

const isServer = typeof window === 'undefined';
const RESP_TIMEOUT = 5000; // 5 секунд таймаут на IPC для надежности

class CacheClient {
  constructor() {
    this.requests = new Map(); // requestId -> { resolve, timer }

    if (isServer) {
      process.on('message', (msg) => {
        if (!msg || typeof msg !== 'object' || msg.type !== 'CACHE_RES') return;

        const req = this.requests.get(msg.requestId);
        if (req) {
          clearTimeout(req.timer);
          this.requests.delete(msg.requestId);
          req.resolve(msg);
        }
      });
    }
  }

  /**
   * Основная обертка для кэширования запросов
   * @param {Object} options 
   * @param {string} options.key Уникальный ключ кэша
   * @param {Function} options.request Функция, возвращающая Promise с данными
   * @param {number} [options.ttl] Время жизни кэша в секундах
   */
  async cachedRequest({ key, request, ttl }) {
    // Если не сервер — выполняем запрос напрямую
    if (!isServer) {
      return request();
    }

    const requestId = Math.random().toString(36).substring(7);

    try {
      // 1. Спрашиваем у Master
      const response = await this.sendToMaster({
        action: 'GET',
        key,
        requestId
      });

      // 2. Если HIT — возвращаем данные из кэша
      if (response.status === 'HIT' || response.status === 'DONE') {
        return response.data;
      }

      // 3. Если MISS — этот воркер назначен ответственным за выполнение запроса
      let result = null;
      try {
        result = await request();
      } finally {
        // Запрос упал или вернул пустоту — снимаем ожидание у Master, иначе ключ зависнет в pendingRequests
        if (!result) {
          process.send({
            type: 'CACHE_REQ',
            action: 'RELEASE',
            key
          });
        }
      }

      // Кэшируем результат
      if (result) {
        process.send({
          type: 'CACHE_REQ',
          action: 'SET',
          key,
          ttl,
          data: result
        });
      }

      return result;

    } catch (e) {
      console.error(`[CacheClient] Error for key ${key}:`, e.message);
      // В случае любой ошибки (таймаут IPC, ошибка API) — выполняем запрос напрямую
      return await request();
    }
  }

  /**
   * Сигнал на полную очистку кэша
   */
  clearAll() {
    if (isServer) {
      process.send({
        type: 'CACHE_REQ',
        action: 'CLEAR'
      });
    }
  }

  /**
   * Отправка сообщения Master-процессу с ожиданием ответа (Promise)
   */
  sendToMaster(payload) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.requests.delete(payload.requestId);
        reject(new Error('IPC_TIMEOUT'));
      }, RESP_TIMEOUT);

      this.requests.set(payload.requestId, { resolve, timer });

      process.send({
        type: 'CACHE_REQ',
        ...payload
      });
    });
  }
}

const client = new CacheClient();

export const cachedRequest = client.cachedRequest.bind(client);
export const clearServerCache = client.clearAll.bind(client);
